import { getPrimaryUnitCatalog, requiredNumber, requiredString } from "common";
import {
    AdjustmentOperation,
    AdjustmentType,
    CatalogItem,
    CouponAlgorithm,
    CouponScope,
    Experience,
    LineItemCode,
    LineItemTemplate,
    LineItemType,
    PartnerDemographicTerm,
    Product,
    ProductObject,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleInitialSequences,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkPurchaseRule,
    PurchaseRuleShortcut,
    PurchaseRuleShortcutType,
    SelectionState,
    Seller,
    TagId,
    TermsTemplate,
    TermsTemplateFee,
    TermsType,
} from "types";
import { array, object, string } from "yup";
import { isDecimalNumber } from "@/helpers/validationHelper";
import { useEquipmentResourcesWithTemplates } from "@/hooks/resources/useEquipmentResourcesWithTemplates";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { generateProductTermExecptions, getPurchaseRuleInputValues } from "@/models/ProductTermException";
import { getPurchaseRulesByTag } from "@/models/PurchaseRule";
import { getSequence } from "@/models/PurchaseRuleLink";
import {
    generatePartnerDemographicTerms,
    getInitialPartnerDemographicPrices,
    getProductSelection,
    getProductSelectionFromPurchaseRules,
    getTermsType,
    initializeTermsTemplatePurchaseRule,
} from "@/models/TermsTemplatePurchaseRule";

export const termsTemplatePurchaseRuleSchema = object().shape({
    amount: requiredNumber()
        .min(0, "Amount should be a positive number")
        .when("algorithm", {
            is: CouponAlgorithm.PERCENT,
            then: requiredNumber().max(100, "Amount should be less than or equal to 100"),
            otherwise: requiredNumber().test(
                "format",
                "Amount should be a positive number with up to two decimal places",
                (value) => isDecimalNumber(value),
            ),
        }),
    algorithm: requiredString(),
    scope: requiredString(),
});

export const termTemplateValidationSchema = object().shape({
    title: requiredString(),
    discount: object().when("termsType", {
        is: TermsType.BASIC,
        then: termsTemplatePurchaseRuleSchema,
    }),
    commission: object().when("termsType", {
        is: TermsType.BASIC,
        then: termsTemplatePurchaseRuleSchema,
    }),
    partnerDemographicTerms: array().when("termsType", {
        is: TermsType.ADVANCED,
        then: array().min(1, "is required"),
    }),
});

export const productTermExceptionValidationSchema = object().shape({
    products: array().of(string()).required().min(1),
    discount: termsTemplatePurchaseRuleSchema,
    commission: termsTemplatePurchaseRuleSchema,
});

/**
 * Fetch line item template by type (discount or commission)
 */
export const useLineItemTemplate = (
    lineItemType: LineItemType.AFFILIATE_COMMISSION | LineItemType.AFFILIATE_DISCOUNT,
) => {
    const {
        data: [lineItemTemplate],
        isLoading,
    } = useCollection<LineItemTemplate>("lineItemTemplates", { type: lineItemType, limit: 1 });

    if (isLoading) {
        return { data: null, isLoading };
    }

    return { data: lineItemTemplate, isLoading };
};

/**
 * Fetch purchase rule by line item type, for a terms template
 */
const useFindPurchaseRule = (
    lineItemType: LineItemType.AFFILIATE_COMMISSION | LineItemType.AFFILIATE_DISCOUNT,
    termsType: TermsType,
    purchaseRules: PurchaseRule[],
): PurchaseRule | null => {
    const seller = useSeller();
    const { data: lineItemTemplate } = useLineItemTemplate(lineItemType);

    if (!lineItemTemplate || !purchaseRules) {
        return null;
    }

    if (termsType === TermsType.ADVANCED) {
        return initializeTermsTemplatePurchaseRule(seller, lineItemTemplate);
    }

    const termsTemplatePurchaseRule = purchaseRules.find((purchaseRule) => {
        const isGivenTemplateRule = purchaseRule.actions[0]?.template?.id === lineItemTemplate.id;
        const overridedRule = purchaseRule.tags?.some((tag) => tag.id === TagId.AFFILIATE_OVERRIDE);

        return isGivenTemplateRule && !overridedRule;
    });

    return termsTemplatePurchaseRule ?? initializeTermsTemplatePurchaseRule(seller, lineItemTemplate);
};

/**
 * Convert terms template (purchase rule link) to form values
 */
export const useFormValues = (
    purchaseRules: PurchaseRule[],
    termsTemplate?: PurchaseRuleLink,
): TermsTemplate | null => {
    const termsType = getTermsType(termsTemplate);
    const { data: demographicTemplates } = useAutoFetchCollection<LineItemTemplate>(["lineItemTemplates"], {
        type: LineItemType.DEMOGRAPHIC,
    });
    // Resolved from live resources: line item templates outlive the equipment they belong to, so a raw
    // template fetch would resurrect deleted equipment in the saved selection.
    const equipmentTemplateIds = useEquipmentResourcesWithTemplates().map((resource) => resource.templateId);
    const { data: activeProducts } = useAutoFetchCollection<Product>("products", {
        "object[in]": ProductObject.EXPERIENCE,
    });

    const discountPurchaseRule = useFindPurchaseRule(LineItemType.AFFILIATE_DISCOUNT, termsType, purchaseRules);
    const commissionPurchaseRule = useFindPurchaseRule(LineItemType.AFFILIATE_COMMISSION, termsType, purchaseRules);

    const discountPurchaseRuleAdvance = getPurchaseRulesByTag(LineItemType.AFFILIATE_DISCOUNT, purchaseRules);

    const selectedProducts = getProductSelectionFromPurchaseRules(discountPurchaseRuleAdvance);

    const { data: experiences } = useAutoFetchCollection<Experience>("experiences");

    if (!discountPurchaseRule || !commissionPurchaseRule) {
        return null;
    }

    if (termsType === TermsType.ADVANCED && !discountPurchaseRuleAdvance) {
        return null;
    }

    const actualProductsSelection =
        activeProducts?.length === 0 ? { all: false, selected: [] } : getProductSelection(discountPurchaseRule);

    return {
        id: termsTemplate?.id,
        title: termsTemplate?.name ?? "",
        products: termsType === TermsType.ADVANCED ? selectedProducts : actualProductsSelection,
        discount: getPurchaseRuleInputValues(
            discountPurchaseRule,
            termsTemplate,
            demographicTemplates,
            equipmentTemplateIds,
        ),
        commission: getPurchaseRuleInputValues(
            commissionPurchaseRule,
            termsTemplate,
            demographicTemplates,
            equipmentTemplateIds,
        ),
        termsType: termsType,
        partnerDemographicTerms: generatePartnerDemographicTerms(purchaseRules, experiences),
        productTermExceptions: generateProductTermExecptions(purchaseRules, termsTemplate, demographicTemplates),
    };
};

/**
 * Convert form values to terms template purchase rule
 */
export const getPurchaseRulePayload = (
    products: SelectionState,
    values: TermsTemplateFee,
    seller: Seller,
    lineItemTemplate: LineItemTemplate,
    isProductTermException?: boolean,
): PurchaseRule => {
    const purchaseRule = initializeTermsTemplatePurchaseRule(seller, lineItemTemplate);
    const [action] = purchaseRule.actions;
    action.shortcut!.amount =
        lineItemTemplate.code === LineItemCode.AFFILIATE_DISCOUNT ? -1 * Math.abs(values.amount!) : values.amount!;

    if (!products.all) {
        purchaseRule.filter = {
            object: PurchaseRuleFilterObject.IN_FILTER,
            operator: "in",
            operand: products.selected,
            field: "product.id",
        };
    }

    if (values.algorithm === CouponAlgorithm.PERCENT) {
        action.shortcut!.type = PurchaseRuleShortcutType.PERCENT;
    } else if (values.algorithm === CouponAlgorithm.ABSOLUTE && values.scope === CouponScope.PER_BOOKING) {
        action.shortcut!.type = PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE;
    } else if (
        values.algorithm === CouponAlgorithm.ABSOLUTE &&
        (values.scope === CouponScope.PER_PERSON || values.scope === CouponScope.PER_EQUIPMENT)
    ) {
        action.shortcut!.type = PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;
    }

    if (action.shortcut) {
        action.shortcut.options = getPurchaseRulePayloadShortcutOptions(values, lineItemTemplate.type);
    }

    if (isProductTermException) {
        purchaseRule.tags?.push({ id: TagId.AFFILIATE_OVERRIDE });
    }

    return purchaseRule;
};

/**
 * Convert form values to terms template purchase rule shortcut options
 */
const getPurchaseRulePayloadShortcutOptions = (
    values: TermsTemplateFee,
    lineItemType: LineItemType,
): PurchaseRuleShortcut["options"] => {
    const typesForDiscount = [
        LineItemType.ADJUSTMENT,
        LineItemType.TAXED_ADJUSTMENT,
        LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
    ];

    const typesForCommission = [...typesForDiscount, LineItemType.AFFILIATE_DISCOUNT];

    const isAffiliateDiscount = lineItemType === LineItemType.AFFILIATE_DISCOUNT;

    const isPerEquipment = values.scope === CouponScope.PER_EQUIPMENT;

    // Per-unit scopes target one unit type; a per-reservation rule covers the whole booking, so it has to
    // reach whichever unit type the product priced on.
    const unitTypes = isPerEquipment
        ? [LineItemType.EQUIPMENT]
        : values.scope === CouponScope.PER_PERSON
        ? [LineItemType.DEMOGRAPHIC]
        : [LineItemType.DEMOGRAPHIC, LineItemType.EQUIPMENT];

    const baseTemplateTypes = values.includeAddOns
        ? [
              ...unitTypes,
              LineItemType.ADDON,
              LineItemType.PICKUP,
              LineItemType.PACKAGE_DISCOUNT,
              LineItemType.COUPON_DISCOUNT,
          ]
        : [...unitTypes, LineItemType.PACKAGE_DISCOUNT, LineItemType.COUPON_DISCOUNT];

    const equipmentSelection = values.equipments ?? { all: false, selected: [] };
    // Per-unit scopes narrow to their own unit type; a per-reservation rule covers the whole booking, so
    // both selections feed it.
    const unitSelection = isPerEquipment
        ? equipmentSelection
        : values.scope === CouponScope.PER_PERSON
        ? values.demographics
        : {
              all: values.demographics.all || equipmentSelection.all,
              selected: [...values.demographics.selected, ...equipmentSelection.selected],
          };

    const templateTypes = isAffiliateDiscount ? typesForDiscount : typesForCommission;
    if (unitSelection.all) {
        if (isAffiliateDiscount && values.algorithm === CouponAlgorithm.PERCENT) {
            return {
                templateTypes: values.includeCustomLineItems
                    ? [...baseTemplateTypes, ...templateTypes]
                    : baseTemplateTypes,
                context: PurchaseRuleLinkEntityObject.AFFILIATE,
            };
        }

        return {
            templateTypes: values.includeCustomLineItems ? [...baseTemplateTypes, ...templateTypes] : baseTemplateTypes,
        };
    }

    const options: PurchaseRuleShortcut["options"] = { templateIds: unitSelection.selected };
    const uniqueTemplateTypes = new Set<string | LineItemType>(options.templateTypes ?? []);

    if (values.includeAddOns) {
        uniqueTemplateTypes.add(LineItemType.ADDON);
        uniqueTemplateTypes.add(LineItemType.PICKUP);
    }

    if (values.includeCustomLineItems) {
        for (const type of templateTypes) {
            uniqueTemplateTypes.add(type);
        }
    }

    options.templateTypes = uniqueTemplateTypes.size > 0 ? [...uniqueTemplateTypes] : options.templateTypes;

    // Per-outing pricing zeroes every demographic amount and carries the price on the Fixed Price
    // line item, so a percent of the selected demographics alone would be a percent of zero.
    if (values.algorithm === CouponAlgorithm.PERCENT) {
        options.templateCodes = [LineItemCode.PER_OUTING_PRICE];
    }

    if (isAffiliateDiscount && values.algorithm === CouponAlgorithm.PERCENT) {
        options.context = PurchaseRuleLinkEntityObject.AFFILIATE;
    }

    return options;
};

interface PurchaseRuleLinkPayloadOptions {
    values: TermsTemplate;
    seller: Seller;
    discountPurchaseRule: PurchaseRule;
    commissionPurchaseRule: PurchaseRule;
    productTermExceptionDiscountPurchaseRules: PurchaseRule[];
    productTermExceptionCommissionPurchaseRules: PurchaseRule[];
    termsTemplate?: PurchaseRuleLink;
}

/**
 * Convert form values to terms template purchase rule link
 */
export const getBasicPurchaseRuleLinkPayload = ({
    values,
    seller,
    discountPurchaseRule,
    commissionPurchaseRule,
    productTermExceptionDiscountPurchaseRules,
    productTermExceptionCommissionPurchaseRules,
    termsTemplate,
}: PurchaseRuleLinkPayloadOptions): PurchaseRuleLink => {
    // Add _remove flag to old PurchaseRuleLink PurchaseRules
    const existingPurchaseRules = addRemoveFlagToPurchaseRuleLink(termsTemplate);
    const purchaseRules: PurchaseRuleLinkPurchaseRule[] = [];

    if (existingPurchaseRules) {
        purchaseRules.push(...existingPurchaseRules);
    }

    purchaseRules.push(
        {
            purchaseRule: { id: commissionPurchaseRule.id! },
            sequence: getSequence(LineItemType.AFFILIATE_COMMISSION),
        },
        {
            purchaseRule: { id: discountPurchaseRule.id! },
            sequence: getSequence(LineItemType.AFFILIATE_DISCOUNT),
        },
    );

    // purchase product term exception purchase rules in purchase rule link
    for (const rule of productTermExceptionDiscountPurchaseRules) {
        purchaseRules.push({
            purchaseRule: { id: rule.id! },
            sequence: getSequence(LineItemType.AFFILIATE_DISCOUNT_OVERRIDE),
        });
    }

    for (const rule of productTermExceptionCommissionPurchaseRules) {
        purchaseRules.push({
            purchaseRule: { id: rule.id! },
            sequence: getSequence(LineItemType.AFFILIATE_COMMISSION_OVERRIDE),
        });
    }

    return {
        name: values.title,
        tags: [{ id: "Affiliate" }, { id: TermsType.BASIC }],
        purchaseRules: purchaseRules,
        entities: termsTemplate?.entities ?? [],
        seller: { id: seller.id },
    };
};

/**
 * Convert advence form values to terms template purchase rule link
 */
export const getAdvancedPurchaseRuleLinkPayload = (
    values: TermsTemplate,
    seller: Seller,
    savedAdjusmentIds: ID[],
    savedCommissionIds: ID[],
    termsTemplate?: PurchaseRuleLink,
): PurchaseRuleLink => {
    // Add _remove flag to old PurchaseRuleLink PurchaseRules
    const existingPurchaseRules = addRemoveFlagToPurchaseRuleLink(termsTemplate);
    const purchaseRules: PurchaseRuleLinkPurchaseRule[] = [];

    if (existingPurchaseRules) {
        purchaseRules.push(...existingPurchaseRules);
    }

    for (const id of savedAdjusmentIds) {
        purchaseRules.push({
            purchaseRule: { id: id },
            sequence: PurchaseRuleInitialSequences.PARTNER_FINAL_PRICE_SEQUENCE_START + 100,
        });
    }

    for (const id of savedCommissionIds) {
        purchaseRules.push({
            purchaseRule: { id: id },
            sequence: getSequence(LineItemType.AFFILIATE_COMMISSION),
        });
    }

    return {
        ...(values.id && { id: values.id }),
        name: values.title,
        tags: [{ id: "Affiliate" }, { id: TermsType.ADVANCED }],
        purchaseRules: purchaseRules,
        entities: termsTemplate?.entities ?? [],
        seller: { id: seller.id },
    };
};

/**
 * Add remove flag to true on all Purchase Rule Link
 */
const addRemoveFlagToPurchaseRuleLink = (
    termsTemplate?: PurchaseRuleLink,
): PurchaseRuleLinkPurchaseRule[] | undefined => {
    return termsTemplate?.purchaseRules.map((purchaseRule) => {
        return {
            purchaseRule: { id: purchaseRule.purchaseRulePermanentId! },
            sequence: getSequence(LineItemType.AFFILIATE_COMMISSION),
            id: purchaseRule.id,
            _remove: true,
        };
    });
};

enum PartnerDemographicPriceType {
    DISCOUNT = "discount",
    COMMISSION = "commission",
}

const findProductById = (products: Product[], productId: string): Product | undefined => {
    return products.find((product) => product.id === productId);
};

// This function takes in discount / commission terms template and products to create demographic prices
// output structure : [
//  product: {id: productId},
//  partnerDemographicPrices: {
//      template: {id: templateId} // this to identify demographic
//      discount: DemographicPrice // this will have amount, type (percent / absolute), operation (set/ inc)
//      commission: DemographicPrice // this will have amount, type (percent / absolute), operation (set/ inc)
//  }
// ]
export const generatePartnerDemographicTermsFromDiscountCommission = (
    termsTemplate: TermsTemplate,
    products: Product[],
): PartnerDemographicTerm[] => {
    let productIds = termsTemplate.products.selected;
    if (termsTemplate.products.all && termsTemplate.termsType === TermsType.ADVANCED && termsTemplate.products.data) {
        productIds = termsTemplate.products.data ?? [];
    }

    if (!productIds) return [];

    return productIds
        .map((productId) => {
            const product = findProductById(products, productId);
            if (!product) return null;

            const unitCatalogItems: CatalogItem[] = getPrimaryUnitCatalog(product);
            const partnerDemographicPrices = unitCatalogItems.flatMap((catalogItem) =>
                generatePartnerDemographicPrices(catalogItem, termsTemplate),
            );

            return {
                product: { id: productId },
                partnerDemographicPrices,
            };
        })
        .filter(Boolean) as PartnerDemographicTerm[];
};

const generatePartnerDemographicPrices = (catalogItem: CatalogItem, termsTemplate: TermsTemplate) => {
    if (!catalogItem.template?.id) return [];

    const agentDemographicPrice = {
        template: { id: catalogItem.template.id },
        discount: getAgentDiscountCommissionPrice(catalogItem, termsTemplate, PartnerDemographicPriceType.DISCOUNT),
        commission: getAgentDiscountCommissionPrice(catalogItem, termsTemplate, PartnerDemographicPriceType.COMMISSION),
    };

    return [getInitialPartnerDemographicPrices(catalogItem, agentDemographicPrice)];
};

const getAgentDiscountCommissionPrice = (
    catalogItem: CatalogItem,
    termsTemplate: TermsTemplate,
    type: PartnerDemographicPriceType,
) => {
    const amount = type === "discount" ? termsTemplate.discount.amount : termsTemplate.commission.amount;
    const adjustmentAlgorithm =
        type === "discount" ? termsTemplate.discount.algorithm : termsTemplate.commission.algorithm;
    return {
        template: { id: catalogItem.template!.id },
        adjustmentAmount: amount ?? 0,
        adjustmentOperation: AdjustmentOperation.MARKUP,
        adjustmentType:
            adjustmentAlgorithm === CouponAlgorithm.ABSOLUTE ? AdjustmentType.ABSOLUTE : AdjustmentType.PERCENT,
        catalogItem: catalogItem,
    };
};
